import { usePageTitle } from "@/hooks/usePageTitle";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1776262145093-ab3f69760686?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/>
  </svg>
);

export default function Login() {
  usePageTitle("Sign in");
  const { user, loading } = useAuth();
  if (!loading && user) return <Navigate to="/" replace />;

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    sessionStorage.setItem("lamka_auth_pending", String(Date.now()));
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="login-page" className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#0F0F0F] text-white">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Editorial fashion" className="absolute inset-0 h-full w-full object-cover scale-105 animate-slow-zoom" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-[#0F0F0F]/50 to-[#0F0F0F]/20" />
        <div className="absolute bottom-16 left-14 right-14 fade-up">
          <p className="eyebrow mb-5">Lamka Online · Est. Lamka</p>
          <h2 className="font-display text-5xl leading-[1.05]">The edit of<br /><em className="text-[#C5A059]">beauty & fashion</em><br />from Lamka.</h2>
        </div>
      </div>

      <div className="flex flex-col justify-between px-6 sm:px-14 lg:px-20 py-10">
        <Link to="/" data-testid="login-logo-link" className="font-display text-2xl tracking-[0.2em] uppercase">
          Lamka <span className="text-[#C5A059]">Online</span>
        </Link>

        <div className="max-w-md w-full py-16 fade-up">
          <p className="eyebrow mb-4">Members · Sign in or register</p>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-6">Welcome to the house.</h1>
          <p className="text-white/60 text-sm sm:text-base leading-relaxed mb-12">
            One account across the Lamka Online app and web. New here? Continuing with Google creates your account automatically.
          </p>

          <button
            data-testid="google-signin-button"
            onClick={signIn}
            className="group w-full flex items-center justify-center gap-3 border border-[#C5A059]/60 bg-white text-[#0F0F0F] px-6 py-4 text-sm font-semibold tracking-wide transition-[background-color,transform,box-shadow] duration-300 hover:bg-[#F5EFE6] hover:shadow-[0_0_30px_rgba(197,160,89,0.35)] active:scale-[0.98]"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="mt-10 border-l border-[#C5A059]/40 pl-4">
            <p data-testid="login-note" className="text-xs text-white/45 leading-relaxed">
              Use the same Google account you use in the Lamka Online mobile app to see your profile, likes and notifications.
            </p>
          </div>
        </div>

        <Link to="/" data-testid="login-browse-link" className="text-xs font-mono uppercase tracking-[0.25em] text-white/50 hover:text-[#C5A059] transition-colors">
          ← Continue browsing
        </Link>
      </div>
    </div>
  );
}
